/**
 * Centralização de caminhos (URLs) do sistema.
 *
 * ROOT/estáticos: usados em definições de <Route path={...} /> e em navegação sem parâmetros.
 * Funções (params): usadas em <Link to={...} />, history.push / navigate(...).
 *
 * Convenções:
 * - Paths estáticos: strings constantes (MAIÚSCULO).
 * - Paths com parâmetros:
 *     - `*_ROOT` string com placeholders (`:param`) para <Route path=...>.
 *     - Função homônima (sem `_ROOT`) que recebe os params e retorna a URL concreta.
 */
export const PATHS = {
  AUTH: {
    LOGIN: '/login',
    LOGIN_WITH_LINK_ROOT: '/login/:link',
    LOGIN_WITH_LINK: link => `/login/${link}`,
    LOGIN_CALLBACK: '/login-callback',
    REGISTER: '/register',
    FORGOT_PASSWORD: '/esqueci-a-senha',
    STUDENT_SIGNUP_WITH_LINK_ROOT: '/criar-uma-conta/:link/:formStep',
    STUDENT_SIGNUP_WITH_LINK: (link, formStep) => `/criar-uma-conta/${link}/${formStep}`,
    STUDENT_SIGNUP_ROOT: '/criar-uma-conta/:formStep',
    STUDENT_SIGNUP: formStep => `/criar-uma-conta/${formStep}`,
    STUDENT_SIGNUP_ALUNO_WITH_LINK_ROOT: '/criar-uma-conta/aluno/:link',
    STUDENT_SIGNUP_ALUNO_WITH_LINK: link => `/criar-uma-conta/aluno/${link}`,
    STUDENT_SIGNUP_ALUNO_ROOT: '/criar-uma-conta/aluno/:formStep',
    STUDENT_SIGNUP_ALUNO: formStep => `/criar-uma-conta/aluno/${formStep}`
  },

  LEGAL: {
    HELP: '/ajuda',
    HELP_VIDEO_ROOT: '/ajuda/:id',
    HELP_VIDEO: id => `/ajuda/${id}`,
    TERMS: '/termos-de-uso',
    PRIVACY: '/politica-de-privacidade'
  },

  DASHBOARD: {
    ROOT: '/',
    EVENT_ROOT: '/evento/:eventId',
    EVENT: eventId => `/evento/${eventId}`
  },

  FEED: {
    LIST: '/feed',
    POST_ROOT: '/posts/:slug',
    POST: slug => `/posts/${slug}`
  },

  PROFILE: {
    ROOT: '/profile',
    USER_ROOT: '/usuario/:selectedTab/:id',
    USER: (selectedTab, id) => `/usuario/${selectedTab}/${id}`
  },

  TASKS: {
    LIST: '/minhas-tarefas',
    VIEW_ROOT: '/minhas-tarefas/:taskType/:taskId',
    VIEW: (taskType, taskId) => `/minhas-tarefas/${taskType}/${taskId}`,
    MANAGEMENT_ROOT: '/gestao-de-tarefa/:selectedTab',
    MANAGEMENT: selectedTab => `/gestao-de-tarefa/${selectedTab}`,
    MANAGEMENT_EXCESS_ROOT: '/gestao-de-tarefa/:selectedTab/tarefas',
    MANAGEMENT_EXCESS: selectedTab => `/gestao-de-tarefa/${selectedTab}/tarefas`,
    MANAGEMENT_EXCESS_STUDENT_ROOT: '/gestao-de-tarefa/:selectedTab/tarefas/:studentId',
    MANAGEMENT_EXCESS_STUDENT: (selectedTab, studentId) => `/gestao-de-tarefa/${selectedTab}/tarefas/${studentId}`
  },

  COURSE: {
    LIST: '/curso',
    RECOMMENDED: '/curso/recomendado',
    CERTIFICATE: '/curso/certificado',
    MONITORING_ROOT: '/curso/acompanhamento/:id',
    MONITORING: id => `/curso/acompanhamento/${id}`,
    MODULE_ROOT: '/curso/:courseId/modulo/:moduleId/:selectedTab',
    MODULE: (courseId, moduleId, selectedTab) => `/curso/${courseId}/modulo/${moduleId}/${selectedTab}`,
    ADMIN_LIST: '/curso/admin',
    ADMIN_DETAIL_ROOT: '/curso/admin/:id',
    ADMIN_DETAIL: id => `/curso/admin/${id}`,
    ADMIN_MANAGE_ROOT: '/curso/admin/gerenciar/:courseId/:moduleId/:selectedTab/:disciplineId',
    ADMIN_MANAGE: (courseId, moduleId, selectedTab, disciplineId) => `/curso/admin/gerenciar/${courseId}/${moduleId}/${selectedTab}/${disciplineId}`,
    CREATE_ROOT: '/curso/criar/:step',
    CREATE: step => `/curso/criar/${step}`,
    EDIT_ROOT: '/curso/criar/:step/:id',
    EDIT: (step, id) => `/curso/criar/${step}/${id}`,
    CREATE_ACTIVITY_ROOT: '/curso/atividades/criar/:step/:moduleId/:courseId',
    CREATE_ACTIVITY: (step, moduleId, courseId) => `/curso/atividades/criar/${step}/${moduleId}/${courseId}`,
    EDIT_ACTIVITY_ROOT: '/curso/atividades/criar/:step/:moduleId/:courseId/:activityId',
    EDIT_ACTIVITY: (step, moduleId, courseId, activityId) => `/curso/atividades/criar/${step}/${moduleId}/${courseId}/${activityId}`,
    COURSE_CREATE_PARTICIPANT: '/course/create/participant',
    COURSE_EDIT_PARTICIPANT_ROOT: '/course/edit/participant/:id',
    COURSE_EDIT_PARTICIPANT: id => `/course/edit/participant/${id}`,
    SHOW_ROOT: '/show-course/:id',
    SHOW: id => `/show-course/${id}`,
    SHOW_CLASSROOM_ROOT: '/show-course/:id/classroom',
    SHOW_CLASSROOM: id => `/show-course/${id}/classroom`,
    SHOW_CLASSROOM_LESSON_ROOT: '/show-course/:id/classroom/:lessonId',
    SHOW_CLASSROOM_LESSON: (id, lessonId) => `/show-course/${id}/classroom/${lessonId}`
  },

  LMS: {
    ROOT: '/lms',
    VIEW_MODULE_ROOT: '/lms/visualizar/modulo/:id',
    VIEW_MODULE: id => `/lms/visualizar/modulo/${id}`,
    VIEW_DISCIPLINE_ROOT: '/lms/visualizar/modulo/:id/disciplina/:disciplineId',
    VIEW_DISCIPLINE: (id, disciplineId) => `/lms/visualizar/modulo/${id}/disciplina/${disciplineId}`,
    VIEW_LESSON_ROOT: '/lms/visualizar/modulo/:id/disciplina/:disciplineId/aula/:lessonId',
    VIEW_LESSON: (id, disciplineId, lessonId) => `/lms/visualizar/modulo/${id}/disciplina/${disciplineId}/aula/${lessonId}`,
    MANAGE_MODULES: '/lms/gerenciar/modulos',
    MANAGE_MODULE_ROOT: '/lms/gerenciar/modulo/:id',
    MANAGE_MODULE: id => `/lms/gerenciar/modulo/${id}`,
    MANAGE_MODULE_DISCIPLINE_ROOT: '/lms/gerenciar/modulo/:moduleId/:disciplineId',
    MANAGE_MODULE_DISCIPLINE: (moduleId, disciplineId) => `/lms/gerenciar/modulo/${moduleId}/${disciplineId}`,
    CREATE_MODULE_ROOT: '/lms/criar/modulo/:step/:courseId',
    CREATE_MODULE: (step, courseId) => `/lms/criar/modulo/${step}/${courseId}`,
    EDIT_MODULE_ROOT: '/lms/criar/modulo/:step/:id/:courseId',
    EDIT_MODULE: (step, id, courseId) => `/lms/criar/modulo/${step}/${id}/${courseId}`,
    CREATE_LESSON_ROOT: '/lms/criar/aula/:contentId/:step/:courseId',
    CREATE_LESSON: (contentId, step, courseId) => `/lms/criar/aula/${contentId}/${step}/${courseId}`,
    EDIT_LESSON_ROOT: '/lms/criar/aula/:contentId/:step/:lessonId/:courseId',
    EDIT_LESSON: (contentId, step, lessonId, courseId) => `/lms/criar/aula/${contentId}/${step}/${lessonId}/${courseId}`
  },

  CLASSROOM: {
    LIST: '/classrooms',
    TURMA: '/turma',
    LIST_TURMAS: '/turmas',
    MANAGE_ROOT: '/turmas/gerenciar/:classroomId',
    MANAGE: classroomId => `/turmas/gerenciar/${classroomId}`,
    INVITES: '/turmas/convites',
    INVITE_ROOT: '/turmas/convites/:inviteId',
    INVITE: inviteId => `/turmas/convites/${inviteId}`,
    IMPORTS: '/turmas/importar',
    IMPORT_ROOT: '/turmas/importar/:importId',
    IMPORT: importId => `/turmas/importar/${importId}`,
    BY_COURSE_INSTITUTION_ROOT: '/classrooms/:courseId/:institutionId',
    BY_COURSE_INSTITUTION: (courseId, institutionId) => `/classrooms/${courseId}/${institutionId}`,
    MANAGE_PARTICIPANTS_ROOT: '/classrooms/:classroomId/gerenciar',
    MANAGE_PARTICIPANTS: classroomId => `/classrooms/${classroomId}/gerenciar`
  },

  EXAM: {
    LIST: '/avaliacoes',
    MY_RESULTS: '/avaliacoes/meus-resultados',
    BY_TAB_ROOT: '/avaliacoes/:selectedTab',
    BY_TAB: selectedTab => `/avaliacoes/${selectedTab}`,
    PRINT_ROOT: '/avaliacoes/:selectedTab/impressao/:id',
    PRINT: (selectedTab, id) => `/avaliacoes/${selectedTab}/impressao/${id}`,
    OFFLINE_ROOT: '/avaliacoes/:selectedTab/offline/:id',
    OFFLINE: (selectedTab, id) => `/avaliacoes/${selectedTab}/offline/${id}`,
    ABOUT_ROOT: '/avaliacoes/sobre/:id',
    ABOUT: id => `/avaliacoes/sobre/${id}`,

    EXAMS_BY_ID_ROOT: '/provas/:id',
    EXAMS_BY_ID: id => `/provas/${id}`,
    EXAMS_CREATE_ROOT: '/provas/criar/:step',
    EXAMS_CREATE: step => `/provas/criar/${step}`,
    EXAMS_EDIT_ROOT: '/provas/criar/:step/:id',
    EXAMS_EDIT: (step, id) => `/provas/criar/${step}/${id}`,
    EXAMS_MANAGE: '/provas/gerenciar',
    EXAMS_MANAGE_BY_QB_ROOT: '/provas/gerenciar/:questionbookId',
    EXAMS_MANAGE_BY_QB: questionbookId => `/provas/gerenciar/${questionbookId}`,
    EXAMS_MANAGE_BY_TAX_ROOT: '/provas/gerenciar/:questionbookId/:taxonomyId',
    EXAMS_MANAGE_BY_TAX: (questionbookId, taxonomyId) => `/provas/gerenciar/${questionbookId}/${taxonomyId}`,
    EXAMS_RESULTS: '/provas/resultados',
    EXAMS_QUESTION_COMMENT_ROOT: '/provas/banco-de-questoes/comentar/:questionId',
    EXAMS_QUESTION_COMMENT: questionId => `/provas/banco-de-questoes/comentar/${questionId}`,

    MOCK_BY_ID_ROOT: '/simulados/:id',
    MOCK_BY_ID: id => `/simulados/${id}`,
    MOCK_LIST: '/simulados',
    MOCK_ABOUT_ROOT: '/simulados/sobre/:id',
    MOCK_ABOUT: id => `/simulados/sobre/${id}`,
    MOCK_CREATE_ROOT: '/simulados/criar/:step',
    MOCK_CREATE: step => `/simulados/criar/${step}`,
    MOCK_EDIT_ROOT: '/simulados/criar/:step/:id',
    MOCK_EDIT: (step, id) => `/simulados/criar/${step}/${id}`,
    MOCK_MANAGE: '/simulados/gerenciar',
    MOCK_MANAGE_BY_QB_ROOT: '/simulados/gerenciar/:questionbookId',
    MOCK_MANAGE_BY_QB: questionbookId => `/simulados/gerenciar/${questionbookId}`,
    MOCK_MANAGE_BY_TAX_ROOT: '/simulados/gerenciar/:questionbookId/:taxonomyId',
    MOCK_MANAGE_BY_TAX: (questionbookId, taxonomyId) => `/simulados/gerenciar/${questionbookId}/${taxonomyId}`,

    WORKBOOK_CREATE_ROOT: '/apostilas/criar/:step',
    WORKBOOK_CREATE: step => `/apostilas/criar/${step}`,
    WORKBOOK_EDIT_ROOT: '/apostilas/criar/:step/:id',
    WORKBOOK_EDIT: (step, id) => `/apostilas/criar/${step}/${id}`,
    WORKBOOK_MANAGE: '/apostilas/gerenciar',
    WORKBOOK_MANAGE_BY_ID_ROOT: '/apostilas/gerenciar/:questionbookId',
    WORKBOOK_MANAGE_BY_ID: questionbookId => `/apostilas/gerenciar/${questionbookId}`,
    WORKBOOK_MANAGE_BY_TAX_ROOT: '/apostilas/gerenciar/:questionbookId/:taxonomyId',
    WORKBOOK_MANAGE_BY_TAX: (questionbookId, taxonomyId) => `/apostilas/gerenciar/${questionbookId}/${taxonomyId}`,

    RESULT_LIST: '/resultado-das-provas/',
    RESULT_DETAIL_ROOT: '/lista-resultado-das-provas/:id',
    RESULT_DETAIL: id => `/lista-resultado-das-provas/${id}`
  },

  QUESTIONS: {
    CREATE_ROOT: '/questoes/criar/:step',
    CREATE: step => `/questoes/criar/${step}`,
    EDIT_ROOT: '/questoes/criar/:step/:id',
    EDIT: (step, id) => `/questoes/criar/${step}/${id}`,
    TAB_ROOT: '/questions/:selectedTab',
    TAB: selectedTab => `/questions/${selectedTab}`,
    TAB_DETAIL_ROOT: '/questions/:selectedTab/:id',
    TAB_DETAIL: (selectedTab, id) => `/questions/${selectedTab}/${id}`,
    REVIEW_QUEUE: '/questoes/revisao',
    REVIEW_MATRIX: '/questoes/revisao/matriz',
    REVIEW_DASHBOARD: '/questoes/revisao/dashboard',
    REVIEW_ITEM_ROOT: '/questoes/revisao/:itemReviewId',
    REVIEW_ITEM: itemReviewId => `/questoes/revisao/${itemReviewId}`
  },

  ESSAY: {
    LIST: '/redacao',
    ABOUT_ROOT: '/redacao/sobre/:essayId',
    ABOUT: essayId => `/redacao/sobre/${essayId}`,
    MONITORING_ROOT: '/redacao/acompanhamento/:essayId',
    MONITORING: essayId => `/redacao/acompanhamento/${essayId}`,
    PRINT_ROOT: '/redacao/impressao/:essayId',
    PRINT: essayId => `/redacao/impressao/${essayId}`,
    CREATE_ROOT: '/redacao/criar/:step',
    CREATE: step => `/redacao/criar/${step}`,
    EDIT_ROOT: '/redacao/editar/:step/:essayId',
    EDIT: (step, essayId) => `/redacao/editar/${step}/${essayId}`,
    SEND_ROOT: '/redacao/enviar/:essayId',
    SEND: essayId => `/redacao/enviar/${essayId}`,
    SEND_BY_STUDENT_ROOT: '/redacao/enviar/:essayId/:studentId',
    SEND_BY_STUDENT: (essayId, studentId) => `/redacao/enviar/${essayId}/${studentId}`,
    CONSULT_ROOT: '/redacao/consulta/:essayId/:studentId',
    CONSULT: (essayId, studentId) => `/redacao/consulta/${essayId}/${studentId}`,
    CORRECTION_ROOT: '/redacao/correcao/:studentId/:essayId',
    CORRECTION: (studentId, essayId) => `/redacao/correcao/${studentId}/${essayId}`,
    VIEW_ROOT: '/redacao/visualizar/:studentId/:essayId',
    VIEW: (studentId, essayId) => `/redacao/visualizar/${studentId}/${essayId}`
  },

  PROJECTS: {
    LIST: '/projetos',
    DETAIL_ROOT: '/projetos/:projectId',
    DETAIL: projectId => `/projetos/${projectId}`,
    ABOUT_ROOT: '/projetos/:projectId/sobre',
    ABOUT: projectId => `/projetos/${projectId}/sobre`,
    CREATE_ROOT: '/projetos/criar/passo/:step',
    CREATE: step => `/projetos/criar/passo/${step}`,
    EDIT_ROOT: '/projetos/criar/passo/:step/:id',
    EDIT: (step, id) => `/projetos/criar/passo/${step}/${id}`,
    GROUPS: '/projetos/grupos',
    GROUP_DETAIL_ROOT: '/projetos/grupos/:id',
    GROUP_DETAIL: id => `/projetos/grupos/${id}`,
    GROUP_PROJECT_ABOUT_ROOT: '/projetos/grupos/:groupId/:projectId/sobre',
    GROUP_PROJECT_ABOUT: (groupId, projectId) => `/projetos/grupos/${groupId}/${projectId}/sobre`,
    INDIVIDUALS: '/projetos/individuais',
    INDIVIDUAL_DETAIL_ROOT: '/projetos/individuais/:id',
    INDIVIDUAL_DETAIL: id => `/projetos/individuais/${id}`,
    PERSONALIZED: '/projetos/personalizados'
  },

  COMPLEMENTARY_ACTIVITY: {
    LIST: '/atividade-complementar',
    BY_COURSE_ROOT: '/atividade-complementar/:activityId/:courseId',
    BY_COURSE: (activityId, courseId) => `/atividade-complementar/${activityId}/${courseId}`,
    DETAIL_ROOT: '/atividade-complementar/atividade/:id',
    DETAIL: id => `/atividade-complementar/atividade/${id}`,
    EVALUATION_ROOT: '/atividade-complementar/avaliacao/:activityId/:id',
    EVALUATION: (activityId, id) => `/atividade-complementar/avaliacao/${activityId}/${id}`,
    EVALUATION_ARCHIVE_ROOT: '/atividade-complementar/avaliar-atividades/:id',
    EVALUATION_ARCHIVE: id => `/atividade-complementar/avaliar-atividades/${id}`,
    EVALUATE_ROOT: '/atividade-complementar/avaliar/:activityId/:id',
    EVALUATE: (activityId, id) => `/atividade-complementar/avaliar/${activityId}/${id}`,
    DATA_ROOT: '/atividade-complementar/dados/:id',
    DATA: id => `/atividade-complementar/dados/${id}`
  },

  AUDITORIUM: {
    LIST: '/auditorio',
    DETAIL_ROOT: '/auditorio/:auditoriumId',
    DETAIL: auditoriumId => `/auditorio/${auditoriumId}`,
    CREATE_ROOT: '/auditorio/criar/:step',
    CREATE: step => `/auditorio/criar/${step}`,
    EDIT_ROOT: '/auditorio/editar/:step/:id',
    EDIT: (step, id) => `/auditorio/editar/${step}/${id}`
  },

  LIVE_CLASSES: {
    LIST: '/aulas-ao-vivo',
    DETAIL_ROOT: '/aulas-ao-vivo/:liveClassId',
    DETAIL: liveClassId => `/aulas-ao-vivo/${liveClassId}`
  },

  WEEK_CHALLENGE: {
    LIST: '/desafio-da-semana',
    CREATE: '/desafio-da-semana/criar',
    EDIT_ROOT: '/desafio-da-semana/editar/:challengeId',
    EDIT: challengeId => `/desafio-da-semana/editar/${challengeId}`,
    BY_TAB_ROOT: '/desafio-da-semana/:selectedTab',
    BY_TAB: selectedTab => `/desafio-da-semana/${selectedTab}`,
    CHALLENGE_ROOT: '/desafio-da-semana/:selectedTab/:challengeId',
    CHALLENGE: (selectedTab, challengeId) => `/desafio-da-semana/${selectedTab}/${challengeId}`,
    PARTICIPANT_RESULT_ROOT: '/desafio-da-semana/:selectedTab/:studentId/:profile/:challengeId',
    PARTICIPANT_RESULT: (selectedTab, studentId, profile, challengeId) => `/desafio-da-semana/${selectedTab}/${studentId}/${profile}/${challengeId}`
  },

  LIVE_CHALLENGE: {
    ADMIN_LIST: '/admin/desafio-ao-vivo',
    ADMIN_CREATE_ROOT: '/admin/desafio-ao-vivo/criar/:step',
    ADMIN_CREATE: step => `/admin/desafio-ao-vivo/criar/${step}`,
    ADMIN_EDIT_ROOT: '/admin/desafio-ao-vivo/editar/:step/:id',
    ADMIN_EDIT: (step, id) => `/admin/desafio-ao-vivo/editar/${step}/${id}`,
    ADMIN_PANEL_ROOT: '/admin/desafio-ao-vivo/painel/:id',
    ADMIN_PANEL: id => `/admin/desafio-ao-vivo/painel/${id}`,
    JOIN: '/desafio-ao-vivo/entrar',
    WAITING_ROOT: '/desafio-ao-vivo/:id/aguardando',
    WAITING: id => `/desafio-ao-vivo/${id}/aguardando`,
    QUESTION_ROOT: '/desafio-ao-vivo/:id/questao',
    QUESTION: id => `/desafio-ao-vivo/${id}/questao`,
    RESULT_ROOT: '/desafio-ao-vivo/:id/resultado',
    RESULT: id => `/desafio-ao-vivo/${id}/resultado`,
    FINAL_ROOT: '/desafio-ao-vivo/:id/final',
    FINAL: id => `/desafio-ao-vivo/${id}/final`
  },

  DUELS: {
    LIST: '/duelos',
    DETAIL_ROOT: '/duelos/:duelId',
    DETAIL: duelId => `/duelos/${duelId}`
  },

  QUIZ: {
    LIST: '/questionario',
    DATA_ROOT: '/questionario/dados/:id',
    DATA: id => `/questionario/dados/${id}`,
    STUDENT_ROOT: '/questionario/:representation/:surveyId/estudante/:studentId',
    STUDENT: (representation, surveyId, studentId) => `/questionario/${representation}/${surveyId}/estudante/${studentId}`,
    CREATE_ROOT: '/questionario/criar/:representation/:id',
    CREATE: (representation, id) => `/questionario/criar/${representation}/${id}`,
    BY_REP_ROOT: '/questionario/:representation/:id',
    BY_REP: (representation, id) => `/questionario/${representation}/${id}`,
    OPEN_ANSWERS_ROOT: '/questionario/dados/:surveyId/propostas/:questionId',
    OPEN_ANSWERS: (surveyId, questionId) => `/questionario/dados/${surveyId}/propostas/${questionId}`
  },

  STUDY_PLAN: {
    ROOT: '/plano-de-estudos',
    DETAIL_ROOT: '/plano-de-estudos/:planId',
    DETAIL: planId => `/plano-de-estudos/${planId}`,
    BLOCK_ROOT: '/plano-de-estudos/:planId/bloco-de-estudos/:blockId',
    BLOCK: (planId, blockId) => `/plano-de-estudos/${planId}/bloco-de-estudos/${blockId}`
  },

  STUDY_GUIDE: {
    ROOT: '/guia-de-estudos',
    BLOCK_ROOT: '/guia-de-estudos/bloco-de-estudo/:blockId',
    BLOCK: blockId => `/guia-de-estudos/bloco-de-estudo/${blockId}`,
    HOW_IT_WORKS: '/guia-de-estudos/funcionamento',
    SETTINGS: '/guia-de-estudos/configuracao'
  },

  LEARNING_PATH: {
    ROOT: '/trilha',
    CURRENT: '/trilha/atual',
    CURRENT_BY_ID_ROOT: '/trilha/atual/:userPathId',
    CURRENT_BY_ID: userPathId => `/trilha/atual/${userPathId}`,
    BLOCK_ROOT: '/trilha/bloco/:blockId',
    BLOCK: blockId => `/trilha/bloco/${blockId}`
  },

  MATERIALS: {
    LIST: '/materiais',
    DETAIL_ROOT: '/materiais/:materialId/:moduleId',
    DETAIL: (materialId, moduleId) => `/materiais/${materialId}/${moduleId}`
  },

  METRICS: {
    LIST_V1: '/metricas-1',
    TAB_ROOT: '/metricas/:selectedTab',
    TAB: selectedTab => `/metricas/${selectedTab}`,
    TAB_REPORT_ROOT: '/metricas/:selectedTab/:reportId',
    TAB_REPORT: (selectedTab, reportId) => `/metricas/${selectedTab}/${reportId}`,
    INSTITUTIONAL_CLASSROOM_ROOT: '/metricas/institucional/engajamento/turma/:classroomId',
    INSTITUTIONAL_CLASSROOM: classroomId => `/metricas/institucional/engajamento/turma/${classroomId}`,
    PROFILE_ROOT: '/metricas/perfil/:id',
    PROFILE: id => `/metricas/perfil/${id}`
  },

  PARTICIPANTS: {
    LIST: '/participantes',
    NOTIFICATIONS: '/participantes/notificacoes',
    NOTIFICATIONS_EXTRACT: '/participantes/notificacoes/extrato',
    ADMIN_PROFILES: '/participantes/perfis-administrativos',
    ADMIN_PROFILE_ROOT: '/participantes/perfis-administrativos/:id',
    ADMIN_PROFILE: id => `/participantes/perfis-administrativos/${id}`
  },

  BANNERS: {
    LIST: '/banners',
    CREATE: '/banners/novo',
    EDIT_ROOT: '/banners/novo/:id',
    EDIT: id => `/banners/novo/${id}`
  },

  ADMIN: {
    ROOT: '/admin'
  },

  LESSON_PLAN: {
    ROOT: '/plano-de-aulas/:selectedTab',
    START: '/plano-de-aulas/inicio',
    FAVORITES: '/plano-de-aulas/favoritos',
    TAB: slug => `/plano-de-aulas/${slug}`
  },

  REPORTS: {
    SEDUC_PI: '/relatorio/seduc-pi',
    SEDUC_PI_GERAL: '/relatorio/seduc-pi/geral',
    SEDUC_PI_GRE: (greId, questionBookId) => `/relatorio/seduc-pi/geral/gre/${greId}${questionBookId ? `/${questionBookId}` : ''}`,
    SEDUC_PI_GRE_ROOT: '/relatorio/seduc-pi/geral/gre/:greId',
    SEDUC_PI_GRE_QB_ROOT: '/relatorio/seduc-pi/geral/gre/:greId/:questionBookId',

    LIVE_CLASSES: '/relatorio/aula-ao-vivo',
    RECORD_CLASSES: '/relatorio/aulas-gravadas',
    REGISTRATION: '/relatorio/cadastro',
    ACCESS: '/relatorio/acesso',
    COURSE_PROGRESS: '/relatorio/progresso-de-curso',
    QUESTIONS: '/relatorio/relatorio-de-perguntas',
    QUIZ: '/relatorio/quiz',
    LIVE_ACCESS: '/relatorio/acessos-aulas-ao-vivo',
    ENEM: '/relatorio/enem',
    ACCESS_FUNNEL: '/relatorio/funil-de-acessos',
    ADHERENCE: '/relatorio/adesao',
    ESSAY: '/relatorio/redacao',
    ESSAY_STUDENT: id => `/relatorio/redacao/aluno/${id}`,
    ESSAY_STUDENT_ROOT: '/relatorio/redacao/aluno/:studentId',
    LEVELING_TEST: '/relatorio/teste-de-nivelamento',
    UPTIME_MONTHLY: '/relatorio/disponibilidade',
    PLATFORM_USAGE: '/relatorio/uso-da-plataforma',
    PERFORMANCE: '/relatorio/performance',
    LIVE_TRANSMISSION: '/relatorio/transmissao-ao-vivo',
    INTERVENTION_NECESSITY: '/relatorio/necessidade-de-intervencao',
    STUDY_HOURS: '/relatorio/horas-de-estudo',
    LEVELING_TEST: '/relatorio/teste-de-nivelamento'
  },
  MISC: {
    JOURNEY: '/jornada',
    SYLLABUS_SUBJECTS: '/ementa/materias',
    SYLLABUS_QUESTION_BANK: '/ementa/banco-de-questoes',
    SYLLABUS_PUBLIC_QUESTIONS: '/ementa/questoes-publicas',
    SYLLABUS_QUESTION_BOOK_ROOT: '/ementa/cadernos-de-questoes/:questionBookId',
    SYLLABUS_QUESTION_BOOK: questionBookId => `/ementa/cadernos-de-questoes/${questionBookId}`,
    COMPONENTS: '/componentes',
    DESIGN_SYSTEM: '/design-system',
    SCHEDULE: '/grade-horaria',
    HEALTHCHECK: '/healthcheck',
    NOT_FOUND: '*'
  },

  SELETIVO: {
    PUBLIC_FORM_ROOT: '/inscricao/:slug',
    PUBLIC_FORM: slug => `/inscricao/${slug}`,
    PUBLIC_SUCCESS_ROOT: '/inscricao/:slug/sucesso',
    PUBLIC_SUCCESS: slug => `/inscricao/${slug}/sucesso`,
    PUBLIC_CLOSED_ROOT: '/inscricao/:slug/encerrado',
    PUBLIC_CLOSED: slug => `/inscricao/${slug}/encerrado`
  },

  ADMIN_SELETIVO: {
    LIST: '/admin/seletivos',
    NEW: '/admin/seletivos/novo',
    DETAIL_ROOT: '/admin/seletivos/:id',
    DETAIL: id => `/admin/seletivos/${id}`,
    EDIT_ROOT: '/admin/seletivos/:id/editar',
    EDIT: id => `/admin/seletivos/${id}/editar`
  }
};
